import { ribbon, arc, chord, descending, formatPrefix, interpolate, scaleOrdinal, range } from "d3"
import { deepCopy } from '@/services/helpers'
import colors from '@/services/colors'

class Chord {
    constructor (matrix, groups) {

        this.groups = groups.map(group => {
            return {
                name: group.name,
                endAngle: 0,
                index: group.key,
                startAngle: 0,
                value: 0,
                opacity: 0.8,
                sourceChords: [],
                targetChords: []
            }
        })

        this.chords = {}

        matrix.forEach((row, sourceIndex) => {
            row.forEach((value, targetIndex) => {
                this.chords[`${sourceIndex}-${targetIndex}`] = {
                    source: {
                        endAngle: 0,
                        index: sourceIndex,
                        startAngle: 0,
                        subindex: 0,
                        value: value
                    },
                    target: {
                        endAngle: 0,
                        index: targetIndex,
                        startAngle: 0,
                        subindex: 0,
                        value: value
                    },
                    visible: true,
                    index: `${sourceIndex}-${targetIndex}`
                }
            })
        })

        // Elements synced with DOM
        this.sync = {
            groups: this.groups,
            chords: this.chords
        }

        this.config = {
            height: 800,
            width: 800,
            innerRadius: 350,
            outerRadius: 370,
            padding: 0.03
        }

        this.ribbon = ribbon()

        this.arc = arc()
            .innerRadius(this.config.innerRadius)
            .outerRadius(this.config.outerRadius)

        this.thinArc = arc()
            .innerRadius(this.config.outerRadius)
            .outerRadius(this.config.outerRadius)
        
        this.chord = chord()
            .padAngle(0.05)
            .sortSubgroups(descending)
        
        this.formatValue = formatPrefix(",.0", 1e3)
        
        // Color scale
        this.color = scaleOrdinal()
        .domain(range(this.groups.length))
        .range(colors)
        
        this.update(matrix)
    }

    update (matrix) {
        this.matrix = matrix

        this.clearSongs()

        this.validateMatrix()

        this.renderGroups()
        this.generateChords()

        this.groupTween = interpolate(this.groups, this.newGroups)
        this.chordTween = interpolate(this.chords, this.newChords)

        this.start = null
        
        // Synced attributes (value)

        this.groups.forEach((group, i) => {
            this.sync.groups[i].value = this.newGroups[i].value            
        })

        Object.keys(this.newChords).forEach(i => {
            this.sync.chords[i].source.value = this.newChords[i].source.value
            this.sync.chords[i].target.value = this.newChords[i].target.value 
        })

        // Animated attributes (startAngle, endAngle)
        requestAnimationFrame((timestamp) => this.step(timestamp))
    }

    addSongHierarchy (song) {
        this.clearSongs()
        this.addSongHierarchy_(song)
    }

    addSongHierarchy_ (song) {
        song.children.forEach(child => {
            this.addSong(child, song)

            if (child.children && child.children.length > 0) {
                this.addSongHierarchy_(child)
            }
        })
    }

    addSong(child, parent) {

        let width = 0.02

        let sourceIndex = this.findGroupBySong(parent).index
        let targetIndex = this.findGroupBySong(child).index
        
        let startDelta = this.newGroups[sourceIndex].endAngle - this.newGroups[sourceIndex].startAngle
        let endDelta = this.newGroups[targetIndex].endAngle - this.newGroups[targetIndex].startAngle

        let startAngle = this.newGroups[sourceIndex].startAngle + (startDelta * Math.max(0, parseInt(parent.year || 0) - 1750) / 365)
        let endAngle = this.newGroups[targetIndex].startAngle + (endDelta * Math.max(0, parseInt(child.year || 0) - 1750) / 365)       

        // Add a chord for a specific song
        this.sync.songChords.push({
            index: child.id,
            title: `${parent.title} (${parent.country_origin} - ${parent.year}) -> ${child.title} (${child.country_origin} - ${child.year})`,
            source: {
                startAngle: startAngle,
                endAngle: startAngle + width,
                index: sourceIndex,
                subIndex: 0,
                value: 1
            },
            target: {
                startAngle: endAngle,
                endAngle: endAngle + width,
                index: targetIndex,
                subIndex: 0,
                value: 1
            }
        })
    }

    clearSongs () {
        this.sync.songChords = []
    }

    step (timestamp) {
        let progress = 0
        if (this.start === null) this.start = timestamp
        progress = Math.min((timestamp - this.start) / 200, 1)  

        this.groups.forEach((group, i) => {
            this.sync.groups[i].startAngle = this.groupTween(progress)[i].startAngle
            this.sync.groups[i].endAngle = this.groupTween(progress)[i].endAngle
        })

        Object.keys(this.newChords).forEach(i => {
            if (this.newChords[i].source.value > 0) {
                this.sync.chords[i].source.startAngle = this.chordTween(progress)[i].source.startAngle
                this.sync.chords[i].source.endAngle = this.chordTween(progress)[i].source.endAngle
                this.sync.chords[i].target.startAngle = this.chordTween(progress)[i].target.startAngle
                this.sync.chords[i].target.endAngle = this.chordTween(progress)[i].target.endAngle
            }
        })

        if (progress < 1) {
            requestAnimationFrame((timestamp) => this.step(timestamp))
        }
    }

    renderGroups () {

        // Copy old data to new data
        this.newGroups = deepCopy(this.groups.map(group => {
            group.value = 0
            group.sourceChords = []
            group.targetChords = []
            return group
        }))

        // Calculate new values
        this.matrix.forEach((group, sourceIndex) => {
            group.forEach((value, targetIndex) => {
                this.newGroups[sourceIndex].value += value
                this.newGroups[targetIndex].value += value
            })
        })

        // Calculate total
        let total = this.newGroups.reduce((acc, g) => {
            return acc += g.value
        }, 0)

        let lastGroup = null
        
        let ratio = (2 * Math.PI - (this.config.padding * this.newGroups.length)) / total

        this.newGroups.forEach((group) => {

            let delta = Math.max(group.value * ratio, 0.01) // Empty groups still have a width
            
            if (lastGroup != null) {
                group.startAngle = lastGroup.endAngle 
                group.startAngle += this.config.padding
            } else {
                group.startAngle = 0
            }

            group.endAngle = group.startAngle + delta

            lastGroup = group
        })
    }

    generateChords () {

        this.newChords = {}
        this.matrix.forEach((row, sourceIndex) => {
            row.forEach((value, targetIndex) => {
                this.newChords[`${sourceIndex}-${targetIndex}`] = {
                    source: {
                        endAngle: 0,
                        index: sourceIndex,
                        startAngle: 0,
                        subindex: 0,
                        value: value
                    },
                    target: {
                        endAngle: 0,
                        index: targetIndex,
                        startAngle: 0,
                        subindex: 0,
                        value: value
                    },
                    visible: true,
                    index: `${sourceIndex}-${targetIndex}`
                }
            })
        })
        
        // Link chords to groups
        Object.values(this.newChords).forEach(chord => {
            this.newGroups[chord.source.index].sourceChords.push({
                chordIndex: chord.index,
                value: chord.source.value
            })

            this.newGroups[chord.target.index].targetChords.push({
                chordIndex: chord.index,
                value: chord.target.value
            })
        })

        // Sort the chords in each group
        this.newGroups.forEach(group => {
            group.sourceChords.sort((a, b) => this.descendingOrder(a, b))
            group.targetChords.sort((a, b) => this.descendingOrder(a, b))
        })

        // Calculate angles
        this.newGroups.forEach(group => {
            let currentAngle = group.startAngle
            let range = group.endAngle - group.startAngle

            for (let j of ['source', 'target']) {
                
                group[`${j}Chords`].forEach(chordData => {
                    let chord = this.newChords[chordData.chordIndex]
                    if (chord) {
                        let delta = (range * chord[j].value / group.value)
                        chord[j].startAngle = currentAngle
                        chord[j].endAngle = currentAngle + delta
                        currentAngle += delta
                    }
                })
            }
        })
    }
    
    descendingOrder(a, b) {
        if (a.value < b.value) {
            return 1
        }
        if (b.value < a.value) {
            return -1
        }
        return 0
    }

    validateMatrix () {
        let valid = false
        for (let i = 0; i < this.matrix.length && !valid; i++) {
            for (let j = 0; j < this.matrix.length && !valid; j++) {
                if (this.matrix[i][j] > 0) {
                    valid = true
                }
            }
        }

        if (!valid) {
            throw "Empty Matrix"
        }
    }

    findGroupBySong(song) {
        let countries = Object.values(this.groups).filter(g => song.country_origin == g.name)

        if (countries.length) {
            return countries[0]
        }

        return Object.values(this.groups)[Object.values(this.groups).length - 1]
    }
}

export default Chord
